import { stripMentionMarkers } from '@design/chat/mention-composer/markers';
import { isPathMatches } from '@terminal/components/app-header/utils/navigation-utils';
import {
  ChatInputConfig,
  ChatSubmitEvent,
  SubmitOptions,
} from '@terminal/components/chat';
import {
  EntityOrThemeWithListTypes,
  isThemeItem,
} from '@terminal/components/search-modal/utils';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useChat } from '@terminal/views/alfred/quick-analysis/use-chat/useChat';
import { useTranslation } from 'react-i18next';

interface UseChatInputHandlersProps {
  config?: ChatInputConfig;
  submitCallback?: (message: string) => void;
  isLoading: boolean;
  trackingMetadata?: TrackingMetadata;
}

export const useChatInputHandlers = ({
  config,
  submitCallback,
  isLoading,
  trackingMetadata,
}: UseChatInputHandlersProps) => {
  const { t } = useTranslation('chat');
  const { trackAlfredQuerySent } = usePosthogEvents();

  const { navigateToAssetOverview } = useNavigateToAssetOverview();
  const { navigateToThemeOverview } = useNavigateToThemeOverview();
  const { resetStore, abortRequest, resetSession, sendMessage } = useChat(
    state => ({
      resetStore: state.resetStore,
      abortRequest: state.abortRequest,
      resetSession: state.resetSession,
      sendMessage: state.sendMessage,
    })
  );

  const shouldResetStore = (question: string) => {
    if (!isPathMatches(['alfred'])) {
      return true;
    }

    const restRegex = new RegExp(`^${t('resetSession')}$`, 'i');
    return restRegex.test(question);
  };

  const submitHandler = (
    inputValue: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    if (isLoading || !inputValue.trim()) {
      return;
    }

    const question = inputValue.trim();
    const trackingValue = stripMentionMarkers(question);

    if (config?.clearDocumentContextOnSubmit) {
      resetSession(true);
    }

    // reset store if asking question not from alfred
    // in other words, init new session
    if (shouldResetStore(trackingValue)) {
      resetStore();
    }

    sendMessage({
      question,
      mentions: options?.mentions,
    });

    const messageId = useChat.getState().messages.at(-1)?.id;

    if (trackingMetadata?.source) {
      trackAlfredQuerySent({
        source: trackingMetadata.source,
        type: 'Quick',
        via: trackingMetadata.via,
        value: trackingValue,
        messageId,
      });
    } else if (isPathMatches(['alfred'])) {
      trackAlfredQuerySent({
        source: POSTHOG_TRACKING_SOURCES.ALFRED,
        type: 'Quick',
        value: trackingValue,
        messageId,
      });
    }

    onSubmitCallbackHandler();
    submitCallback?.(trackingValue);
  };

  const onSelect = (item: EntityOrThemeWithListTypes, clear: () => void) => {
    const isTheme = isThemeItem(item);
    const source = trackingMetadata?.source ?? POSTHOG_TRACKING_SOURCES.ALFRED;

    if (isTheme) {
      navigateToThemeOverview(item.id, source);
    } else {
      navigateToAssetOverview(item.tag, source);
    }

    clear();
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const onSubmit = (
    event: ChatSubmitEvent,
    inputText: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler, options);
  };

  return {
    onSubmit,
    onKeyDown,
    onSelect,
    onAbort: abortRequest,
  };
};
